import React, { useState, useEffect } from 'react';
import { X, ClipboardCheck, CheckCircle, XCircle, AlertTriangle, Printer } from 'lucide-react';
import api from '../../api.js';
import { useFormat } from '../../i18n/format.js';

function InspectionDetailModal({ inspection, onClose, onUpdate, onMessage, vehicles, drivers }) {
  const { t, fmt } = useFormat();
  const [inspectionItems, setInspectionItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load inspection items
  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const response = await api.inspectionItem.getByInspection(inspection.id);
        setInspectionItems(response.data || []);
      } catch (error) {
        console.error('Error loading inspection items:', error);
      } finally {
        setLoading(false);
      }
    };

    if (inspection?.id) {
      loadItems();
    }
  }, [inspection?.id]);

  // Get vehicle info
  const getVehicleInfo = () => {
    const vehicle = vehicles.find(v => v.id === inspection.vehicle_id);
    return vehicle || null;
  };

  // Get driver info
  const getDriverInfo = () => {
    if (!inspection.driver_id) return null;
    return drivers.find(d => d.id === inspection.driver_id) || null;
  };

  // Status helpers
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      passed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => t(`inspections.status.${status}`, { defaultValue: status });

  const getTypeLabel = (type) => t(`inspections.types.${type}`, { defaultValue: type });

  const getSeverityColor = (severity) => {
    const colors = {
      minor: 'bg-yellow-100 text-yellow-800',
      major: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityLabel = (severity) => t(`inspections.severity.${severity}`, { defaultValue: severity });

  // Le libellé stocké en base est la référence FR : il sert de repli.
  const getItemLabel = (item) => t(`inspections.checklist.items.${item.code}`, { defaultValue: item.label });

  // Requalifier une inspection soumise exige un motif : le serveur refuse sans (409
  // INSPECTION_LOCKED) et journalise avec (audit 2026-09-07, lot 2.3).
  const [changeReason, setChangeReason] = useState('');

  // Update inspection status
  const handleUpdateStatus = async (newStatus) => {
    if (!changeReason.trim()) {
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.detail.reasonRequired'),
        type: 'error'
      });
      return;
    }
    try {
      await api.inspection.update(inspection.id, { status: newStatus, change_reason: changeReason.trim() });
      onMessage?.({
        title: t('inspections.messages.successTitle'),
        message: t('inspections.messages.statusUpdated', { status: getStatusLabel(newStatus) }),
        type: 'success'
      });
      onUpdate?.();
      onClose();
    } catch (error) {
      onMessage?.({
        title: t('inspections.messages.errorTitle'),
        message: t('inspections.errors.updateStatusFailed'),
        type: 'error'
      });
    }
  };

  // Group items by category (reserved for future grouped display)
  // eslint-disable-next-line no-unused-vars
  const _groupedItems = inspectionItems.reduce((acc, item) => {
    // Parse category from item if available, otherwise use the generic bucket
    const category = item.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: inspectionItems.length,
    passed: inspectionItems.filter(i => i.status === 'pass').length,
    failed: inspectionItems.filter(i => i.status === 'fail').length,
    na: inspectionItems.filter(i => i.status === 'na').length
  };

  const vehicle = getVehicleInfo();
  const driver = getDriverInfo();

  if (!inspection) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 flex justify-between items-center ${
          inspection.status === 'failed' ? 'bg-red-500' :
          inspection.status === 'passed' ? 'bg-green-500' :
          'bg-indigo-500'
        } text-white`}>
          <div className="flex items-center gap-3">
            {inspection.status === 'failed' ? (
              <XCircle className="w-6 h-6" />
            ) : inspection.status === 'passed' ? (
              <CheckCircle className="w-6 h-6" />
            ) : (
              <ClipboardCheck className="w-6 h-6" />
            )}
            <div>
              <h2 className="text-xl font-bold">
                {t('inspections.detail.title', { id: inspection.id, type: getTypeLabel(inspection.type) })}
              </h2>
              <p className="text-sm opacity-90">
                {inspection.inspection_date} {inspection.inspection_time && t('inspections.detail.atTime', { time: inspection.inspection_time })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="p-2 hover:bg-white/10 rounded-lg"
              title={t('inspections.detail.print')}
            >
              <Printer className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Status Banner */}
          <div className={`p-4 rounded-lg mb-6 ${
            inspection.status === 'failed' ? 'bg-red-50 border-2 border-red-200' :
            inspection.status === 'passed' ? 'bg-green-50 border-2 border-green-200' :
            'bg-blue-50 border-2 border-blue-200'
          }`}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                {inspection.status === 'failed' ? (
                  <XCircle className="w-8 h-8 text-red-500" />
                ) : inspection.status === 'passed' ? (
                  <CheckCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-blue-500" />
                )}
                <div>
                  <div className={`text-2xl font-bold ${
                    inspection.status === 'failed' ? 'text-red-700' :
                    inspection.status === 'passed' ? 'text-green-700' :
                    'text-blue-700'
                  }`}>
                    {getStatusLabel(inspection.status).toUpperCase()}
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('inspections.detail.issuesFound', { count: inspection.issues_found || 0 })}
                  </p>
                </div>
              </div>
              <span className={`px-4 py-2 rounded-full font-medium ${getStatusColor(inspection.status)}`}>
                {getStatusLabel(inspection.status)}
              </span>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Vehicle Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                {t('inspections.detail.vehicleTitle')}
              </h3>
              {vehicle ? (
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-500">{t('inspections.detail.makeModel')}:</span> {vehicle.make} {vehicle.model}</p>
                  <p><span className="text-gray-500">{t('inspections.detail.plate')}:</span> {vehicle.license_plate}</p>
                  <p><span className="text-gray-500">{t('inspections.detail.vin')}:</span> {vehicle.vin || '-'}</p>
                  <p><span className="text-gray-500">{t('inspections.detail.inspectionOdometer')}:</span> {inspection.odometer ? fmt.number(inspection.odometer) : '-'} km</p>
                </div>
              ) : (
                <p className="text-gray-500">{t('inspections.detail.vehicleFallback', { id: inspection.vehicle_id })}</p>
              )}
            </div>

            {/* Driver & Meta Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                {t('inspections.detail.driverTitle')}
              </h3>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">{t('inspections.detail.driver')}:</span>{' '}
                  {driver ? `${driver.first_name} ${driver.last_name}` : '-'}
                </p>
                <p><span className="text-gray-500">{t('inspections.detail.type')}:</span> {getTypeLabel(inspection.type)}</p>
                <p><span className="text-gray-500">{t('inspections.detail.duration')}:</span> {inspection.duration_minutes ? t('inspections.detail.durationValue', { count: inspection.duration_minutes }) : '-'}</p>
                <p><span className="text-gray-500">{t('inspections.detail.formVersion')}:</span> {inspection.form_version || '-'}</p>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-xs text-gray-500">{t('inspections.detail.statsTotal')}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.passed}</div>
              <div className="text-xs text-gray-500">{t('inspections.detail.statsPassed')}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-xs text-gray-500">{t('inspections.detail.statsFailed')}</div>
            </div>
            <div className="bg-gray-50 border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-600">{stats.na}</div>
              <div className="text-xs text-gray-500">{t('common.states.notAvailable')}</div>
            </div>
          </div>

          {/* Inspection Items */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">{t('inspections.detail.itemsTitle')}</h3>

            {loading ? (
              <div className="text-center py-8 text-gray-500">{t('common.states.loading')}</div>
            ) : inspectionItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('inspections.detail.noItems')}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Failed items first */}
                {stats.failed > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-800 mb-3">
                      {t('inspections.detail.anomalies', { count: stats.failed })}
                    </h4>
                    <div className="space-y-2">
                      {inspectionItems.filter(i => i.status === 'fail').map(item => (
                        <div key={item.id} className="flex items-start gap-3 bg-white p-3 rounded-lg">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{getItemLabel(item)}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${getSeverityColor(item.severity)}`}>
                                {getSeverityLabel(item.severity)}
                              </span>
                            </div>
                            {item.notes && (
                              <p className="text-sm text-gray-600 mt-1">{item.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All items table */}
                <div className="bg-white border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('inspections.detail.tableCode')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('inspections.detail.tableItem')}</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('inspections.detail.tableStatus')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('inspections.detail.tableNotes')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {inspectionItems.map(item => (
                        <tr key={item.id} className={item.status === 'fail' ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono">{item.code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{getItemLabel(item)}</td>
                          <td className="px-4 py-3 text-center">
                            {item.status === 'pass' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                <CheckCircle className="w-3 h-3" /> {t('inspections.itemStatus.pass')}
                              </span>
                            )}
                            {item.status === 'fail' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                <XCircle className="w-3 h-3" /> {t('inspections.itemStatus.fail')}
                              </span>
                            )}
                            {item.status === 'na' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                                {t('inspections.itemStatus.na')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with actions */}
        <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
          <div className="text-sm text-gray-500">
            {t('inspections.detail.createdAt', { date: inspection.created_at ? fmt.dateTime(inspection.created_at) : '-' })}
          </div>
          <div className="flex gap-3 items-center">
            {inspection.status === 'submitted' && (
              <>
                <input
                  type="text"
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder={t('inspections.detail.reasonPlaceholder')}
                  className="px-3 py-2 border rounded-lg text-sm w-64"
                  aria-label={t('inspections.detail.reasonPlaceholder')}
                />
                <button
                  onClick={() => handleUpdateStatus('passed')}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('inspections.detail.markPassed')}
                </button>
                <button
                  onClick={() => handleUpdateStatus('failed')}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  {t('inspections.detail.markFailed')}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InspectionDetailModal;
